
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, AlertCircle, Shield } from "lucide-react";
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import VideoRoom from '@/components/videocall/VideoRoom';
import { toast } from 'sonner';
import { format, parseISO, isAfter, isBefore, addMinutes, subMinutes } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const VideoCallPage: React.FC = () => {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [appointment, setAppointment] = useState<any>(null);
  const [role, setRole] = useState<'patient' | 'psychologist' | null>(null);
  const [hasAccess, setHasAccess] = useState(false);
  
  useEffect(() => {
    if (!user || !appointmentId) {
      setError('Usuário não autenticado ou consulta não especificada');
      setLoading(false);
      return;
    }
    
    loadAppointmentData();
  }, [appointmentId, user]);

  const loadAppointmentData = async () => {
    try {
      setLoading(true);
      
      // Buscar dados da consulta
      const { data, error } = await supabase
        .from('appointments')
        .select(`
          id, date, time, duration, status, meet_link, user_id, psychologist_id,
          psychologists(name, user_id),
          profiles(first_name, last_name)
        `)
        .eq('id', appointmentId)
        .single();

      if (error) throw error;
      
      if (!data) {
        setError('Consulta não encontrada');
        return;
      }

      setAppointment(data);
      
      // Determinar o papel do usuário
      let userRole: 'patient' | 'psychologist' | null = null;
      
      if (data.user_id === user.id) {
        userRole = 'patient';
      } else if (data.psychologists?.user_id === user.id) {
        userRole = 'psychologist';
      }
      
      if (!userRole) {
        setError('Você não tem permissão para acessar esta consulta');
        return;
      }
      
      setRole(userRole);
      
      // Verificar se a consulta está confirmada
      if (data.status !== 'confirmed') {
        setError('Apenas consultas confirmadas podem ser acessadas');
        return;
      }
      
      // Verificar se tem link de videochamada
      if (!data.meet_link) {
        setError('Link de videochamada não disponível');
        return;
      }
      
      // Verificar horário de acesso
      const now = new Date();
      const appointmentDateTime = parseISO(`${data.date}T${data.time}`);
      const startTime = subMinutes(appointmentDateTime, 15); // 15 min antes
      const endTime = addMinutes(appointmentDateTime, data.duration + 15); // duração + 15 min depois
      
      if (isBefore(now, startTime)) {
        setError(`Acesso liberado a partir de ${format(startTime, "HH:mm 'do dia' dd/MM/yyyy", { locale: ptBR })}`);
        return;
      }
      
      if (isAfter(now, endTime)) {
        setError('O horário de acesso à consulta expirou');
        return;
      }
      
      setHasAccess(true);
      
    } catch (error) {
      console.error('Error loading appointment:', error);
      setError('Erro ao carregar dados da consulta');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Redirecionar para o dashboard apropriado
    if (role === 'psychologist') {
      navigate('/psychologist-dashboard');
    } else {
      navigate('/patient-dashboard');
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Card className="w-96">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin mb-4 text-primary" />
            <p>Verificando acesso à videochamada...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Card className="w-96">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
            <h2 className="text-lg font-semibold mb-2">Acesso Negado</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate(-1)}>
                Voltar
              </Button>
              <Button onClick={handleClose}>
                Ir para Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!hasAccess || !role) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Card className="w-96">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Shield className="h-12 w-12 text-yellow-500 mb-4" />
            <h2 className="text-lg font-semibold mb-2">Verificando Permissões</h2>
            <p className="text-muted-foreground">Aguarde...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-screen">
      <VideoRoom
        appointmentId={appointmentId || ''}
        userId={user.id}
        role={role}
        onClose={handleClose}
      />
    </div>
  );
};

export default VideoCallPage;
